import { FormatColorText } from '@mui/icons-material';
import { Box, Button, Divider, IconButton, Popover, Stack, Typography } from '@mui/material';
import type { Editor } from '@tiptap/react';
import { useState, type MouseEvent } from 'react';

import { normalizeHexColor } from './documentColors';
import { activeTableStyleColor, applyTableCellColor } from './tableCellColors';
import useTranslate from 'hooks/useTranslate';

const toolbarButtonSx = {
    textTransform: 'none' as const,
    fontWeight: 500,
    minWidth: 28,
    height: 28,
    minHeight: 28,
    px: 0.75,
    py: 0,
    borderRadius: '4px',
    border: 'none',
    color: '#444746',
    bgcolor: 'transparent',
    boxShadow: 'none',
    '& .MuiSvgIcon-root': { fontSize: 18, width: 18, height: 18 },
    '&:hover': {
        bgcolor: 'rgba(68, 71, 70, 0.08)',
        boxShadow: 'none',
    },
};

const TEXT_COLORS = [
    '#111827',
    '#374151',
    '#ffffff',
    '#b91c1c',
    '#c2410c',
    '#15803d',
    '#1d4ed8',
    '#6d28d9',
] as const;

const BACKGROUND_COLORS = [
    '#fef3c7',
    '#ffedd5',
    '#dcfce7',
    '#dbeafe',
    '#fce7f3',
    '#ede9fe',
    '#f3f4f6',
    '#ffffff',
] as const;

const TABLE_BACKGROUND_COLORS = [
    '#111827',
    '#1e3a5f',
    '#8353e2',
    '#0f766e',
    '#b91c1c',
    '#f3f4f6',
    '#fff7ed',
    '#ffffff',
] as const;

interface ColorSectionProps {
    label: string;
    colors: readonly string[];
    value: string | null;
    fallback: string;
    customLabel: string;
    defaultLabel: string;
    disabled?: boolean;
    onChange: (color: string | null) => void;
}

function ColorSection({
    label,
    colors,
    value,
    fallback,
    customLabel,
    defaultLabel,
    disabled = false,
    onChange,
}: ColorSectionProps) {
    return (
        <Box sx={disabled ? { opacity: 0.45, pointerEvents: 'none' } : undefined}>
            <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, mb: 0.75 }}>
                {label}
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(8, 22px)', gap: 0.5 }}>
                {colors.map((color) => {
                    const selected = value === color;
                    return (
                        <IconButton
                            key={color}
                            size="small"
                            aria-label={color}
                            aria-pressed={selected}
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={() => onChange(color)}
                            sx={{
                                width: 22,
                                height: 22,
                                p: 0,
                                borderRadius: '4px',
                                bgcolor: color,
                                border: '1px solid #d1d5db',
                                boxShadow: selected ? '0 0 0 2px #fff, 0 0 0 4px #8353e2' : 'none',
                                '&:hover': { bgcolor: color },
                            }}
                        />
                    );
                })}
            </Box>
            <Stack
                direction="row"
                sx={{ alignItems: 'center', justifyContent: 'space-between', mt: 1 }}
            >
                <Typography variant="caption" color="text.secondary">
                    {customLabel}
                </Typography>
                <Box
                    component="label"
                    sx={{
                        position: 'relative',
                        width: 22,
                        height: 22,
                        borderRadius: '4px',
                        border: '1px solid #d1d5db',
                        bgcolor: value || fallback,
                        overflow: 'hidden',
                        cursor: 'pointer',
                    }}
                >
                    <Box
                        component="input"
                        type="color"
                        aria-label={customLabel}
                        value={value || fallback}
                        onChange={(event) => onChange(event.target.value)}
                        sx={{
                            position: 'absolute',
                            opacity: 0,
                            inset: 0,
                            width: '100%',
                            height: '100%',
                            p: 0,
                            border: 0,
                            cursor: 'pointer',
                        }}
                    />
                </Box>
            </Stack>
            <Button
                size="small"
                variant="text"
                disabled={disabled}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => onChange(null)}
                sx={{ mt: 0.5, px: 0, minWidth: 0, textTransform: 'none' }}
            >
                {defaultLabel}
            </Button>
        </Box>
    );
}

function markColor(editor: Editor, attribute: 'color' | 'backgroundColor'): string | null {
    const value = editor.getAttributes('textStyle')[attribute];
    return typeof value === 'string' ? normalizeHexColor(value) : null;
}

interface ColorControlProps {
    editor: Editor;
}

export function ColorControl({ editor }: ColorControlProps) {
    const { translate } = useTranslate();
    const [anchor, setAnchor] = useState<HTMLElement | null>(null);
    const inTable = editor.isActive('table');
    const textColor = markColor(editor, 'color');
    const textBackground = markColor(editor, 'backgroundColor');
    const tableBackground = inTable
        ? activeTableStyleColor(editor.state, 'background-color')
        : null;
    const tableTextColor = inTable ? activeTableStyleColor(editor.state, 'color') : null;

    const keepSelection = (event: MouseEvent) => {
        const target = event.target as HTMLElement;
        if (target.closest('input')) return;
        event.preventDefault();
    };

    const applyTextColor = (color: string | null) => {
        const chain = editor.chain().focus();
        if (color) chain.setColor(color).run();
        else chain.unsetColor().run();
    };

    const applyTextBackground = (color: string | null) => {
        const chain = editor.chain().focus();
        if (color) chain.setBackgroundColor(color).run();
        else chain.unsetBackgroundColor().run();
    };

    return (
        <>
            <Button
                variant="text"
                color="inherit"
                size="small"
                aria-label={translate('editorColors')}
                onMouseDown={(event) => event.preventDefault()}
                onClick={(event) => setAnchor(event.currentTarget)}
                startIcon={<FormatColorText />}
                sx={toolbarButtonSx}
            >
                {translate('editorColors')}
            </Button>
            <Popover
                open={Boolean(anchor)}
                anchorEl={anchor}
                onClose={() => setAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                disableAutoFocus
                disableEnforceFocus
                disableRestoreFocus
                slotProps={{
                    paper: {
                        onMouseDown: keepSelection,
                        sx: { p: 1.5, width: 248, maxHeight: 480, overflowY: 'auto' },
                    },
                }}
            >
                <Stack spacing={1.25} divider={<Divider flexItem />}>
                    <ColorSection
                        label={translate('editorTextColor')}
                        colors={TEXT_COLORS}
                        value={textColor}
                        fallback="#111827"
                        customLabel={translate('editorCustomColor')}
                        defaultLabel={translate('editorColorDefault')}
                        onChange={applyTextColor}
                    />
                    <ColorSection
                        label={translate('editorTextBackground')}
                        colors={BACKGROUND_COLORS}
                        value={textBackground}
                        fallback="#fef3c7"
                        customLabel={translate('editorCustomColor')}
                        defaultLabel={translate('editorColorDefault')}
                        onChange={applyTextBackground}
                    />
                    <Box>
                        {inTable ? null : (
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: 'block', mb: 1 }}
                            >
                                {translate('editorTableColorHint')}
                            </Typography>
                        )}
                        <Stack spacing={1.25}>
                            <ColorSection
                                label={translate('editorTableBackground')}
                                colors={TABLE_BACKGROUND_COLORS}
                                value={tableBackground}
                                fallback="#f3f4f6"
                                customLabel={translate('editorCustomColor')}
                                defaultLabel={translate('editorColorDefault')}
                                disabled={!inTable}
                                onChange={(color) =>
                                    applyTableCellColor(editor, 'background-color', color)
                                }
                            />
                            <ColorSection
                                label={translate('editorTableTextColor')}
                                colors={TEXT_COLORS}
                                value={tableTextColor}
                                fallback="#111827"
                                customLabel={translate('editorCustomColor')}
                                defaultLabel={translate('editorColorDefault')}
                                disabled={!inTable}
                                onChange={(color) => applyTableCellColor(editor, 'color', color)}
                            />
                        </Stack>
                    </Box>
                </Stack>
            </Popover>
        </>
    );
}
